// ============================================================================
// PairsForm - Protected Layout avec Sidebar + Mobile BottomNav
// ============================================================================

import { useState } from "react";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { MobileNavigation } from "./MobileNavigation";
import { PublicFooter } from "./PublicFooter";
import { OrgVisitBanner } from "../OrgVisitBanner";
import { ConditionsUsageDialog } from "../ConditionsUsageDialog";
import { QuickActionsFAB } from "../QuickActionsFAB";
import { NotificationBell } from "../NotificationBell";
import { useNavigation } from "../../hooks/useNavigation";
import { useCasquetteImplicite } from "../../hooks/useCasquetteImplicite";
import { useIsMobile } from "../../hooks/useIsMobile";

interface ProtectedLayoutProps {
  children: React.ReactNode;
}

export function ProtectedLayout({ children }: ProtectedLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Bascule implicite de casquette (FX 2026-08-19) : si la route courante
  // relève d'un autre membership de la personne (participante ici, animatrice
  // là), on re-signe le JWT avant de rendre la page — elle n'a jamais à savoir
  // que nos activités vivent dans des organisations distinctes.
  const { menuSections } = useNavigation();
  const basculeEnCours = useCasquetteImplicite(menuSections);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar Desktop */}
      {!isMobile && <Sidebar variant="permanent" />}

      {/* Sidebar Mobile (drawer temporaire, ouvert via hamburger) */}
      {isMobile && (
        <Sidebar
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
        />
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: "background.default",
          minHeight: "100vh",
          overflowX: "hidden",
          pb: isMobile ? "80px" : 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Hamburger button mobile — affiché en haut à gauche */}
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: "fixed",
              top: 8,
              left: 8,
              zIndex: 1100,
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <Menu size={22} />
          </IconButton>
        )}

        {/* Cloche de notifications in-app (haut-droite, fixe) */}
        <NotificationBell />

        {/* Bannière de visite d'org (SUPER_ADMIN) */}
        <OrgVisitBanner />

        {/* Conditions d'usage à accepter par le pilote de l'org (lot 8.5) */}
        <ConditionsUsageDialog />

        <Box sx={{ flex: 1 }}>
          {basculeEnCours ? (
            // On sursoit au rendu le temps de la bascule : sans ça la page
            // partirait chercher ses données avec l'ancien jeton et
            // s'afficherait vide avant de se corriger.
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            children
          )}
        </Box>

        {/* Footer global — visible sur toutes les pages authentifiées */}
        <PublicFooter />

        {/* Bouton hexagonal turquoise — accès rapide veille, actions, support,
            audit. Phase bêta : son entrée « Faire une suggestion » ouvre le
            dialog retours bêta (URL capturée) au lieu du formulaire support. */}
        <QuickActionsFAB />
      </Box>

      {/* Bottom Navigation mobile */}
      {isMobile && <MobileNavigation />}
    </Box>
  );
}
